import {
  FileArchive, FileAudio, FileImage, FileSpreadsheet, FileText, FileVideo,
  File as FileGlyph, type LucideIcon,
} from 'lucide-react';

/**
 * Extension -> lucide component. Falls back to a generic file glyph.
 * Match by URL suffix (case-insensitive) or by a MIME string if the caller has one.
 * Keep the list small; add here rather than inlining a switch at each site.
 */
const BY_EXT: Record<string, LucideIcon> = {
  pdf: FileText,
  doc: FileText, docx: FileText, rtf: FileText, txt: FileText, md: FileText,
  xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet, tsv: FileSpreadsheet,
  png: FileImage, jpg: FileImage, jpeg: FileImage, gif: FileImage, webp: FileImage, svg: FileImage, heic: FileImage,
  mp4: FileVideo, mov: FileVideo, webm: FileVideo, mkv: FileVideo,
  mp3: FileAudio, wav: FileAudio, m4a: FileAudio, ogg: FileAudio,
  zip: FileArchive, tar: FileArchive, gz: FileArchive, rar: FileArchive, '7z': FileArchive,
};

export function iconForFile(input?: string): LucideIcon {
  if (!input) return FileGlyph;
  const s = input.toLowerCase();
  const mimeMatch = /^([a-z]+)\/([a-z0-9.+-]+)$/i.exec(s);
  if (mimeMatch) {
    // Regex has two capture groups so both matches are guaranteed non-empty;
    // noUncheckedIndexedAccess still widens the tuple, so assert.
    const top = mimeMatch[1]!;
    const sub = mimeMatch[2]!;
    if (top === 'image') return FileImage;
    if (top === 'video') return FileVideo;
    if (top === 'audio') return FileAudio;
    if (sub === 'pdf') return FileText;
    if (sub === 'zip' || sub === 'x-zip-compressed' || sub === 'x-tar' || sub === 'gzip') return FileArchive;
    if (sub === 'vnd.ms-excel' || sub === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet' || sub === 'csv') return FileSpreadsheet;
    if (sub.includes('word') || sub === 'plain') return FileText;
    return FileGlyph;
  }
  // .split() may return an empty array only on empty string; s.length > 0 here since input was checked.
  const parts = s.split('?')[0]?.split('#')[0]?.split('.') ?? [];
  const ext = parts.length > 1 ? parts[parts.length - 1] : undefined;
  return (ext && BY_EXT[ext]) || FileGlyph;
}
